import PropTypes from 'prop-types';
import React from 'react';
import {compose} from 'redux';
import {connect} from 'react-redux';
import ReactModal from 'react-modal';
import VM from 'scratch-vm';
import {injectIntl, intlShape} from 'react-intl';

import ErrorBoundaryHOC from '../lib/error-boundary-hoc.jsx';
import {
    getIsError,
    getIsShowingProject
} from '../reducers/project-state';
import {
    activateTab,
    BLOCKS_TAB_INDEX,
    COSTUMES_TAB_INDEX,
    SOUNDS_TAB_INDEX
} from '../reducers/editor-tab';

import {
    closeCostumeLibrary,
    closeBackdropLibrary,
    closeTelemetryModal,
    closeVisionTrainer,
    closeCodeLibrary,
    openExtensionLibrary,
    closeDebugModal,
    openVisionTrainer,
    openCodeLibrary
} from '../reducers/modals';

import FontLoaderHOC from '../lib/font-loader-hoc.jsx';
import LocalizationHOC from '../lib/localization-hoc.jsx';
import SBFileUploaderHOC from '../lib/sb-file-uploader-hoc.jsx';
import ProjectFetcherHOC from '../lib/project-fetcher-hoc.jsx';
import TitledHOC from '../lib/titled-hoc.jsx';
import ProjectSaverHOC from '../lib/project-saver-hoc.jsx';
import QueryParserHOC from '../lib/query-parser-hoc.jsx';
import storage from '../lib/storage';
import vmListenerHOC from '../lib/vm-listener-hoc.jsx';
import vmManagerHOC from '../lib/vm-manager-hoc.jsx';
import cloudManagerHOC from '../lib/cloud-manager-hoc.jsx';
import systemPreferencesHOC from '../lib/system-preferences-hoc.jsx';

import GUIComponent from '../components/gui/gui.jsx';
import {setIsScratchDesktop} from '../lib/isScratchDesktop.js';

const {RequestMetadata, setMetadata, unsetMetadata} = storage.scratchFetch;

const setProjectIdMetadata = projectId => {
    // If project ID is '0' or zero, it's not a real project ID. In that case, remove the project ID metadata.
    // Same if it's null undefined.
    if (projectId && projectId !== '0') {
        setMetadata(RequestMetadata.ProjectId, projectId);
    } else {
        unsetMetadata(RequestMetadata.ProjectId);
    }
};

class GUI extends React.Component {
    componentDidMount () {
        setIsScratchDesktop(this.props.isScratchDesktop);
        this.props.onStorageInit(storage);
        this.props.onVmInit(this.props.vm);
        setProjectIdMetadata(this.props.projectId);
        this.props.vm.runtime.on('VISION_TRAINER_OPEN', this.props.onOpenVisionTrainer);
        this.props.vm.runtime.on('VISION_TRAINER_CLOSE', this.props.onCloseVisionTrainer);

        if (!this.props.vm.extensionManager.isExtensionLoaded('visionClassifier')) {
            this.props.vm.extensionManager.loadExtensionURL('visionClassifier');
        }
        if (!this.props.vm.extensionManager.isExtensionLoaded('microbit')) {
            this.props.vm.extensionManager.loadExtensionURL('microbit');
        }

    }
    componentDidUpdate (prevProps) {
        if (this.props.projectId !== prevProps.projectId) {
            if (this.props.projectId !== null) {
                this.props.onUpdateProjectId(this.props.projectId);
            }
            setProjectIdMetadata(this.props.projectId);
        }
        if (this.props.isShowingProject && !prevProps.isShowingProject) {
            // this only notifies container when a project changes from not yet loaded to loaded
            // At this time the project view in www doesn't need to know when a project is unloaded
            this.props.onProjectLoaded();
        }
        if (this.props.shouldStopProject && !prevProps.shouldStopProject) {
            this.props.vm.stopAll();
        }
    }
    componentWillUnmount () {
        this.props.vm.runtime.removeListener('VISION_TRAINER_OPEN', this.props.onOpenVisionTrainer);
        this.props.vm.runtime.removeListener('VISION_TRAINER_CLOSE', this.props.onCloseVisionTrainer);
    }
    render () {
        if (this.props.isError) {
            throw new Error(
                `Error in Scratch GUI [location=${window.location}]: ${this.props.error}`);
        }
        const {
            /* eslint-disable no-unused-vars */
            assetHost,
            cloudHost,
            error,
            isError,
            isScratchDesktop,
            isShowingProject,
            onProjectLoaded,
            onStorageInit,
            onUpdateProjectId,
            onVmInit,
            projectHost,
            projectId,
            /* eslint-enable no-unused-vars */
            children,
            fetchingProject,
            isLoading,
            loadingStateVisible,
            ...componentProps
        } = this.props;
        return (
            <GUIComponent
                loading={fetchingProject || isLoading || loadingStateVisible}
                {...componentProps}
            >
                {children}
            </GUIComponent>
        );
    }
}

GUI.propTypes = {
    assetHost: PropTypes.string,
    children: PropTypes.node,
    cloudHost: PropTypes.string,
    error: PropTypes.oneOfType([PropTypes.object, PropTypes.string]),
    fetchingProject: PropTypes.bool,
    intl: intlShape,
    isError: PropTypes.bool,
    isLoading: PropTypes.bool,
    isScratchDesktop: PropTypes.bool,
    isShowingProject: PropTypes.bool,
    isTotallyNormal: PropTypes.bool,
    loadingStateVisible: PropTypes.bool,
    onProjectLoaded: PropTypes.func,
    onCloseCodeLibrary: PropTypes.func,
    onCloseVisionTrainer: PropTypes.func,
    onOpenCodeLibrary: PropTypes.func,
    onOpenVisionTrainer: PropTypes.func,
    onSeeCommunity: PropTypes.func,
    onStorageInit: PropTypes.func,
    onUpdateProjectId: PropTypes.func,
    onVmInit: PropTypes.func,
    projectHost: PropTypes.string,
    projectId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    shouldStopProject: PropTypes.bool,
    telemetryModalVisible: PropTypes.bool,
    codeLibraryVisible: PropTypes.bool,
    vm: PropTypes.instanceOf(VM).isRequired
};

GUI.defaultProps = {
    isScratchDesktop: false,
    isTotallyNormal: false,
    onStorageInit: storageInstance => storageInstance.addOfficialScratchWebStores(),
    onProjectLoaded: () => {},
    onUpdateProjectId: () => {},
    onVmInit: (/* vm */) => {}
};

const mapStateToProps = state => {
    const loadingState = state.scratchGui.projectState.loadingState;
    return {
        activeTabIndex: state.scratchGui.editorTab.activeTabIndex,
        alertsVisible: state.scratchGui.alerts.visible,
        backdropLibraryVisible: state.scratchGui.modals.backdropLibrary,
        blocksTabVisible: state.scratchGui.editorTab.activeTabIndex === BLOCKS_TAB_INDEX,
        cardsVisible: state.scratchGui.cards.visible,
        connectionModalVisible: state.scratchGui.modals.connectionModal,
        costumeLibraryVisible: state.scratchGui.modals.costumeLibrary,
        costumesTabVisible: state.scratchGui.editorTab.activeTabIndex === COSTUMES_TAB_INDEX,
        debugModalVisible: state.scratchGui.modals.debugModal,
        error: state.scratchGui.projectState.error,
        isError: getIsError(loadingState),
        isFullScreen: state.scratchGui.mode.isFullScreen,
        isPlayerOnly: state.scratchGui.mode.isPlayerOnly,
        isRtl: state.locales.isRtl,
        isShowingProject: getIsShowingProject(loadingState),
        loadingStateVisible: state.scratchGui.modals.loadingProject,
        projectId: state.scratchGui.projectState.projectId,
        soundsTabVisible: state.scratchGui.editorTab.activeTabIndex === SOUNDS_TAB_INDEX,
        targetIsStage: (
            state.scratchGui.targets.stage &&
            state.scratchGui.targets.stage.id === state.scratchGui.targets.editingTarget
        ),
        telemetryModalVisible: state.scratchGui.modals.telemetryModal,
        tipsLibraryVisible: state.scratchGui.modals.tipsLibrary,
        codeLibraryVisible: state.scratchGui.modals.codeLibrary,
        visionTrainerVisible: state.scratchGui.modals.visionTrainer,
        vm: state.scratchGui.vm
    };
};

const mapDispatchToProps = dispatch => ({
    onExtensionButtonClick: () => dispatch(openExtensionLibrary()),
    onMicrobitButtonClick: async (vm, isConnected) => {
        dispatch(activateTab(BLOCKS_TAB_INDEX));
        if (!vm.extensionManager.isExtensionLoaded('microbit')) {
            await vm.extensionManager.loadExtensionURL('microbit');
        }

        const peripheral = vm.runtime && vm.runtime.peripheralExtensions &&
            vm.runtime.peripheralExtensions.microbit;
        const radioGatewayConnected = peripheral &&
            typeof peripheral.isRadioGatewayConnected === 'function' &&
            peripheral.isRadioGatewayConnected();

        if (isConnected || radioGatewayConnected) {
            vm.disconnectPeripheral('microbit');
            return;
        }

        if (peripheral && typeof peripheral.connectRadioGateway === 'function') {
            await peripheral.connectRadioGateway();
            return;
        }

        vm.scanForPeripheral('microbit');
    },
    onMicrobitBleButtonClick: async (vm, isConnected) => {
        dispatch(activateTab(BLOCKS_TAB_INDEX));
        if (!vm.extensionManager.isExtensionLoaded('microbit')) {
            await vm.extensionManager.loadExtensionURL('microbit');
        }
        const peripheral = vm.runtime && vm.runtime.peripheralExtensions &&
            vm.runtime.peripheralExtensions.microbit;
        if (isConnected) {
            vm.disconnectPeripheral('microbit');
            return;
        }
        if (peripheral && typeof peripheral.connectOfficialBluetooth === 'function') {
            await peripheral.connectOfficialBluetooth();
            return;
        }
        vm.scanForPeripheral('microbit');
    },
    onPrepareMicrobitPeripheral: async vm => {
        dispatch(activateTab(BLOCKS_TAB_INDEX));
        if (!vm.extensionManager.isExtensionLoaded('microbit')) {
            await vm.extensionManager.loadExtensionURL('microbit');
        }

        return vm.runtime && vm.runtime.peripheralExtensions &&
            vm.runtime.peripheralExtensions.microbit;
    },
    onActivateTab: tab => dispatch(activateTab(tab)),
    onActivateCostumesTab: () => dispatch(activateTab(COSTUMES_TAB_INDEX)),
    onActivateSoundsTab: () => dispatch(activateTab(SOUNDS_TAB_INDEX)),
    onCloseVisionTrainer: () => dispatch(closeVisionTrainer()),
    onCloseCodeLibrary: () => dispatch(closeCodeLibrary()),
    onOpenCodeLibrary: () => dispatch(openCodeLibrary()),
    onOpenVisionTrainer: () => dispatch(openVisionTrainer()),
    onRequestCloseBackdropLibrary: () => dispatch(closeBackdropLibrary()),
    onRequestCloseCostumeLibrary: () => dispatch(closeCostumeLibrary()),
    onRequestCloseDebugModal: () => dispatch(closeDebugModal()),
    onRequestCloseTelemetryModal: () => dispatch(closeTelemetryModal()),
    onRequestCloseVisionTrainer: () => dispatch(closeVisionTrainer()),
    onRequestCloseCodeLibrary: () => dispatch(closeCodeLibrary())
});

const ConnectedGUI = injectIntl(connect(
    mapStateToProps,
    mapDispatchToProps
)(GUI));

// note that redux's 'compose' function is just being used as a general utility to make
// the hierarchy of HOC constructor calls clearer here; it has nothing to do with redux's
// ability to compose reducers.
const WrappedGui = compose(
    LocalizationHOC,
    ErrorBoundaryHOC('Top Level App'),
    FontLoaderHOC,
    QueryParserHOC,
    ProjectFetcherHOC,
    TitledHOC,
    ProjectSaverHOC,
    vmListenerHOC,
    vmManagerHOC,
    SBFileUploaderHOC,
    cloudManagerHOC,
    systemPreferencesHOC
)(ConnectedGUI);

WrappedGui.setAppElement = ReactModal.setAppElement;
export default WrappedGui;
